import { useQuery } from '@tanstack/react-query'
import { repository } from '../data'
import { Icon } from './icons'
import { Thumb } from './Photos'
import { Badge, EmptyState, PageHeader, Panel } from './primitives'
import { Link } from './shell/Link'

const STATUS: Readonly<Record<string, { label: string; tone: 'healthy' | 'warning' | 'critical' | 'neutral' }>> = {
  in_service: { label: 'In service', tone: 'healthy' },
  restricted: { label: 'Restricted', tone: 'warning' },
  grounded: { label: 'Grounded', tone: 'critical' },
  depot: { label: 'At the depot', tone: 'neutral' },
  retired: { label: 'Retired', tone: 'neutral' },
}

/** Fleet (§10.1): every robot, where it is, and whether anything is missing from it. */
export function FleetView(): JSX.Element {
  const q = useQuery({ queryKey: ['fleet'], queryFn: () => repository.fleet() })
  const robots = q.data ?? []

  return (
    <div className="page">
      <PageHeader
        title="Fleet"
        subtitle={q.isSuccess ? `${robots.length} ${robots.length === 1 ? 'robot' : 'robots'}` : undefined}
        actions={
          <Link to={{ name: 'scan', unknown: null }} className="button">
            <Icon name="scan" size={18} />
            Scan a label
          </Link>
        }
      />
      {q.isPending ? <p className="muted">Loading…</p> : null}
      {q.isError ? <p className="critical">Could not load the fleet.</p> : null}
      {q.isSuccess ? (
        <Panel>
          {robots.length === 0 ? (
            <EmptyState icon="fleet" title="No robots yet">
              A robot is taken in like any other part, then commissioned with a fleet number.
            </EmptyState>
          ) : (
            <div className="row-list">
              {robots.map((r) => {
                const missing = r.configured - r.fitted
                return (
                  <Link key={r.id} to={{ name: 'component', id: r.componentId }} className="row-item">
                    <span className="row" style={{ gap: 12, minWidth: 0 }}>
                      {r.imageId ? <Thumb imageId={r.imageId} label={r.fleetNumber} size={40} /> : null}
                      <span className="row-item__main">
                        <span className="row-item__title">{r.fleetNumber}</span>
                        <span className="row-item__meta">
                          <span className="serial">{r.serial}</span>
                          {r.site ? ` · ${r.site}` : ''}
                        </span>
                      </span>
                    </span>
                    <span className="row-item__side">
                      <Badge tone={STATUS[r.status]?.tone ?? 'neutral'}>{STATUS[r.status]?.label ?? r.status}</Badge>
                      {missing > 0 ? <Badge tone="warning">{missing} missing</Badge> : null}
                    </span>
                  </Link>
                )
              })}
            </div>
          )}
        </Panel>
      ) : null}
    </div>
  )
}
